import { useState, useEffect, useContext } from 'react';
import {
    Alert,
    Box,
    Close,
    Heading,
    Text,
    Spinner,
    Flex
} from '@theme-ui/components';
import { SkipIdentity, Alert as StyledAlert } from '~/Common'
import { Icon } from 'assets/Icon';
import * as Yup from 'yup';
import { Submit } from '~/Forms/Submit';
import { Field } from '~/Forms/Field';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { OnboardingContext } from 'pages/Onboarding/Landing';
import { useDispatch, connect } from 'react-redux';
import { onboardingVerifyId, getOnboardingStatusAsync } from 'features/auth/authSlice';
import { utilStateByCountry } from 'features/theme/themeSlice';
import { toast } from 'utils/toast';
import moment from 'moment';
import ReactMarkdown from 'react-markdown'
import { Themed } from 'theme-ui';

const mapStateToProps = (state) => {
    return { user: state.user?.user, settings: state.theme?.settings }
}

const validationSchema = Yup.object().shape({
    driver_licence_givenname: Yup.string().required('required field'),
    driver_licence_middlename: Yup.string().nullable(),
    driver_licence_surname: Yup.string().required('required field'),
    driver_licence_state: Yup.string().required('required field'),
    driver_licence_dob: Yup.string().test(
            'driver_licence_dob',
            'you must be at least 18',
            (value) => {
                return (
                    moment().diff(moment(value), 'years') >=
                    18
                );
            }
        )
        .required('required field'),
    driver_licence_number: Yup.string().required('required field').trim()
        .matches(/^[a-zA-Z0-9]{1,10}$/, 'Please enter a valid Australian Driver\'s Licence Number'),
    driver_licence_expiry: Yup.string().test(
        'driver_licence_expiry',
        'Expiry date can be up to 6 months expired',
        (value) => moment(value).isAfter(moment().subtract(6, 'months'))
    ).required('required field'),
    driver_licence_cardnumber: Yup.string()
        .required('required field')
        .trim()
        .matches(/^[a-zA-Z0-9]{8,10}$/, 'Please enter a valid card number'),
    driver_licence_tandc: Yup.boolean().oneOf([true], 'You must accept the terms and conditions before we can send your data')
});

const OnboardingDriversLicence = ({ user, settings }) => {
    const onboarding = useContext(OnboardingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [stateOptions, setStateOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        utilStateByCountry('AU').then((response) => {
            setStateOptions(response?.data);
        }).catch(({ response }) => {
            setStateOptions(null);
        }).finally(() => setLoading(false));
    }, []);

    const handleSubmit = (values, actions) => {
        onboardingVerifyId({
            source: 'driver_licence',
            driver_licence_givenname: values.driver_licence_givenname,
            driver_licence_middlename: values.driver_licence_middlename,
            driver_licence_surname: values.driver_licence_surname,
            driver_licence_dob: values.driver_licence_dob,
            driver_licence_number: values.driver_licence_number,
            driver_licence_expiry: values.driver_licence_expiry,
            driver_licence_cardnumber: values.driver_licence_cardnumber,
            driver_licence_state: values.driver_licence_state,
            driver_licence_tandc: values.driver_licence_tandc ? 'on' : null
        }).then((response) => {
            toast.success(t('onboarding.landing.drivers_licence.success'), {
                toastId: 'drivers_licence-success',
            });
            onboarding.nextStep();
            dispatch(getOnboardingStatusAsync());
        }).catch(({ response }) => {
            response?.data?.errors && actions.setErrors(response.data.errors);
            toast.error(response?.data?.message || t('onboarding.landing.drivers_licence.error'), {
                toastId: 'drivers_licence-error',
            });
            actions.setStatus('api_error');
            actions.setSubmitting(false);
        }).finally(() => setLoading(false));
    };

    return (
        <>
            {loading && <Spinner variant="styles.spinner" m={'auto'} />}

            <Box sx={{
                display: loading ? 'none' : 'block'
            }}>
                <Formik
                    enableReinitialize
                    initialValues={{
                        driver_licence_number: '',
                        driver_licence_cardnumber: '',
                        driver_licence_givenname: user?.first_name ?? '',
                        driver_licence_middlename: user?.middle_names ?? '',
                        driver_licence_surname: user?.last_name ?? '',
                        driver_licence_dob: user?.individual?.dob ?? '',
                        driver_licence_state: user?.individual?.address?.state ?? '',
                        driver_licence_expiry: '',
                        driver_licence_tandc: false
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ status, setStatus }) => (
                        <Box>
                            { status === 'api_error' ?
                            <Alert role="alert" variant='danger' mb={2}>
                                {t('onboarding.landing.drivers_licence.error')}
                                <Close ml="auto" mr={-2} onClick={() => setStatus(null)} />
                            </Alert> : ''}

                            <Form>
                                <Flex sx={{
                                    mb: 4,
                                    alignItems: 'center'
                                }}>
                                    <Icon icon="identity-card" size="48px" mr={2} color="tealLight" />
                                    <Heading as="h1" variant="h3" sx={{ fontWeight: 500, lineHeight: 1 }}>
                                        {t('onboarding.landing.drivers_licence.title')}
                                    </Heading>
                                </Flex>

                                <Box mb={4}>
                                    <SkipIdentity sx={{ textAlign: 'left' }}/>
                                </Box>

                                <Text as="p" mb={4} sx={{
                                    fontSize: 2,
                                    color: 'dark'
                                }}>
                                    {t('onboarding.landing.drivers_licence.subtitle')}
                                </Text>

                                <StyledAlert mb={4} variant="info" isCloseable={false}>{t('onboarding.landing.drivers_licence.legal_name')}</StyledAlert>

                                <Field
                                    label="First name"
                                    name="driver_licence_givenname"
                                />
                                <Field
                                    label="Middle names"
                                    name="driver_licence_middlename"
                                />
                                <Field
                                    label="Last name"
                                    name="driver_licence_surname"
                                />

                                <Field
                                    field="date"
                                    label="Date of birth"
                                    name="driver_licence_dob"
                                />

                                {Array.isArray(stateOptions) && stateOptions.length ? (
                                    <Field
                                        field="select"
                                        label="State"
                                        placeholder="-- State --"
                                        options={stateOptions}
                                        name="driver_licence_state"
                                    />
                                ) : <Spinner variant="styles.spinner" />}

                                <Field
                                    label="Licence number"
                                    name="driver_licence_number"
                                />

                                <Field
                                    field="date"
                                    label="Expiry date"
                                    name="driver_licence_expiry"
                                    futureDate={true}
                                />

                                <Field
                                    label="Card number"
                                    name="driver_licence_cardnumber"
                                />

                                <Box sx={{
                                    mb: 4,
                                    fontSize: '13px',
                                    color: 'dark',
                                    '& a': {
                                        color: 'tealDark'
                                    }
                                }}>
                                    {t('The unique 8-10 character card number.')} <Themed.a href="https://www.opy.com/au/how-to-find-your-licence-card-number/" target="_blank">See where this is for your state.</Themed.a>
                                </Box>

                                <Box sx={{
                                    overflow: 'auto',
                                    maxHeight: '300px',
                                    bg: 'light',
                                    border: '1px solid',
                                    borderColor: 'medium',
                                    borderRadius: '4px',
                                    p: 3,
                                    mb: 4,
                                    fontSize: '0.8rem'
                                }}>
                                    <Box sx={{ variant: 'styles' }}>
                                        <ReactMarkdown>{settings.identity_terms_conditions}</ReactMarkdown>
                                    </Box>
                                </Box>

                                <Field
                                    field="checkbox"
                                    label="I agree to the above terms and conditions"
                                    name="driver_licence_tandc"
                                />

                                <Submit variant="primary" text={t('onboarding.landing.drivers_licence.submit')} sx={{ display: 'block', width: '100%' }} />
                            </Form>
                        </Box>
                    )}
                </Formik>
            </Box>
        </>
    )
};

export default connect(mapStateToProps, null)(OnboardingDriversLicence);
